import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Queue as QueueIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  EventRepeat as FollowUpIcon,
  Medication as MedicationIcon,
  PointOfSale as PointOfSaleIcon,
} from '@mui/icons-material';
import {
  patientAPI,
  appointmentAPI,
  visitAPI,
  invoiceAPI,
  paymentAPI,
  followUpAPI,
  medicineAPI,
  dispensingAPI,
  settingsAPI,
  analyticsAPI,
} from '../services/api';
import StatCard from '../components/StatCard';
import PrintPortal from '../components/PrintPortal';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDur = (sec) => {
  if (sec == null) return '-';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};
const stageSeconds = (v, stage) =>
  (v.stages || []).find((s) => s.stage === stage)?.duration ?? null;
const waitAfterStage = (v, stage) => {
  const stages = v.stages || [];
  const i = stages.findIndex((s) => s.stage === stage);
  if (i < 0) return null;
  const s = stages[i];
  const next = stages[i + 1];
  if (!s.endTime || !next?.startTime) return null;
  return Math.max(0, (new Date(next.startTime) - new Date(s.endTime)) / 1000);
};
const waitSeconds = (v) => {
  const stages = v.stages || [];
  let total = 0;
  stages.forEach((s, i) => {
    const next = stages[i + 1];
    if (s.endTime && next?.startTime) {
      total += Math.max(0, (new Date(next.startTime) - new Date(s.endTime)) / 1000);
    }
  });
  return total;
};
const totalSeconds = (v) =>
  (v.stages || []).reduce((s, st) => s + (st.duration || 0), 0) + waitSeconds(v);
const visitFees = (v) => {
  const f = v.fees || {};
  return (f.consultationFee || 0) + (f.optometryFee || 0) + (f.surgeryFee || 0) +
    (f.investigationFee || 0) + (f.otherFees || 0);
};

const REPORTS = [
  {
    key: 'patients',
    label: 'Patient Registrations',
    icon: <PeopleIcon />,
    color: 'primary',
    columns: ['Patient ID', 'Name', 'Age', 'Gender', 'Phone', 'City', 'Registered On', 'Referral Source', 'Medical Conditions'],
    fetch: (p) => patientAPI.getPatients({ dateFrom: p.from, dateTo: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((p) => [
        p.patientId, p.name, p.age, p.gender, p.contact?.phone || '', p.address?.city || '',
        fmtDate(p.createdAt), p.referralSource || '', (p.medicalHistory?.conditions || []).join(', '),
      ]),
    summary: (d) => [
      { label: 'Total Registrations', value: d.length },
      { label: 'Male', value: d.filter((p) => p.gender === 'Male').length },
      { label: 'Female', value: d.filter((p) => p.gender === 'Female').length },
      { label: 'With Medical Conditions', value: d.filter((p) => (p.medicalHistory?.conditions || []).length > 0).length },
    ],
  },
  {
    key: 'appointments',
    label: 'Appointments',
    icon: <CalendarIcon />,
    color: 'info',
    columns: ['Appointment ID', 'Date', 'Time', 'Patient', 'Phone', 'Reason', 'Source', 'Status'],
    fetch: (p) => appointmentAPI.getAppointments({ dateFrom: p.from, dateTo: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((a) => [
        a.appointmentId, fmtDate(a.appointmentDate), a.appointmentTime, a.patient?.name || '',
        a.patient?.contact?.phone || '', a.reason || '', a.source, a.status,
      ]),
    summary: (d) => [
      { label: 'Total', value: d.length },
      { label: 'Booked/Confirmed', value: d.filter((a) => ['booked', 'confirmed'].includes(a.status)).length },
      { label: 'Arrived', value: d.filter((a) => a.status === 'arrived').length },
      { label: 'Cancelled / No-show', value: d.filter((a) => ['cancelled', 'no-show'].includes(a.status)).length },
    ],
  },
  {
    key: 'visits',
    label: 'OPD Visits',
    icon: <QueueIcon />,
    color: 'success',
    columns: ['Visit ID', 'Token', 'Date', 'Patient', 'Chief Complaint', 'Referred To', 'Status', 'Total Fees'],
    fetch: (p) => visitAPI.getVisits({ dateFrom: p.from, dateTo: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((v) => [
        v.visitId, `#${v.tokenNumber ?? '-'}`, fmtDate(v.visitDate), v.patient?.name || '',
        v.chiefComplaint || '', v.referredTo || '', v.status.replace(/-/g, ' '), inr(visitFees(v)),
      ]),
    summary: (d) => [
      { label: 'Total Visits', value: d.length },
      { label: 'Completed', value: d.filter((v) => v.status === 'completed').length },
      { label: 'In Progress', value: d.filter((v) => v.status !== 'completed').length },
      { label: 'Fee Value', value: inr(d.reduce((s, v) => s + visitFees(v), 0)) },
    ],
  },
  {
    key: 'invoices',
    label: 'Revenue (Invoices)',
    icon: <ReceiptIcon />,
    color: 'error',
    columns: ['Invoice No', 'Date', 'Patient', 'Subtotal', 'GST', 'Discount', 'Total', 'Paid', 'Balance', 'Status'],
    fetch: (p) => invoiceAPI.listInvoices({ startDate: p.from, endDate: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((i) => [
        i.invoiceNumber, fmtDate(i.createdAt), i.patient?.name || '', inr(i.subtotal),
        inr(i.totalTax), inr(i.discount), inr(i.totalAmount), inr(i.paidAmount),
        inr(i.balanceAmount), i.paymentStatus,
      ]),
    summary: (d) => [
      { label: 'Invoices', value: d.length },
      { label: 'Billed', value: inr(d.reduce((s, i) => s + (i.totalAmount || 0), 0)) },
      { label: 'Collected', value: inr(d.reduce((s, i) => s + (i.paidAmount || 0), 0)) },
      { label: 'Pending', value: inr(d.reduce((s, i) => s + (i.balanceAmount || 0), 0)) },
      { label: 'GST Collected', value: inr(d.reduce((s, i) => s + (i.totalTax || 0), 0)) },
    ],
  },
  {
    key: 'payments',
    label: 'Collections (Payments)',
    icon: <PaymentsIcon />,
    color: 'warning',
    columns: ['Receipt No', 'Date', 'Patient', 'Amount', 'Mode', 'Type', 'Collected By', 'Notes'],
    fetch: (p) => paymentAPI.getPayments({ startDate: p.from, endDate: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((pay) => [
        pay.receiptNumber, fmtDate(pay.createdAt), pay.patient?.name || '', inr(pay.amount),
        pay.mode, pay.type || '', pay.collectedBy?.name || '', pay.notes || '',
      ]),
    summary: (d) => [
      { label: 'Receipts', value: d.length },
      { label: 'Total Collected', value: inr(d.reduce((s, p) => s + (p.amount || 0), 0)) },
      { label: 'Cash', value: inr(d.filter((p) => p.mode === 'cash').reduce((s, p) => s + p.amount, 0)) },
      { label: 'UPI', value: inr(d.filter((p) => p.mode === 'upi').reduce((s, p) => s + p.amount, 0)) },
      { label: 'Card', value: inr(d.filter((p) => p.mode === 'card').reduce((s, p) => s + p.amount, 0)) },
    ],
  },
  {
    key: 'followups',
    label: 'Follow-up Recall',
    icon: <FollowUpIcon />,
    color: 'info',
    columns: ['Patient', 'Phone', 'Doctor', 'Diagnosis', 'Consulted On', 'Follow-up Due', 'Status', 'Days'],
    fetch: (p) => followUpAPI.getFollowUpsDue({ startDate: p.from, endDate: p.to }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((f) => [
        f.patient?.name || '', f.patient?.contact?.phone || '', f.doctor?.name || '',
        f.diagnosis || '', fmtDate(f.consultationDate), fmtDate(f.followUpDueDate),
        f.recallStatus, f.daysRemaining,
      ]),
    summary: (d) => [
      { label: 'Total Follow-ups', value: d.length },
      { label: 'Overdue', value: d.filter((f) => f.recallStatus === 'overdue').length },
      { label: 'Due Today', value: d.filter((f) => f.recallStatus === 'due-today').length },
      { label: 'Upcoming', value: d.filter((f) => f.recallStatus === 'upcoming').length },
    ],
  },
  {
    key: 'stageTime',
    label: 'Visit Stage Time (per Patient)',
    icon: <QueueIcon />,
    color: 'warning',
    columns: ['Token', 'Visit ID', 'Date', 'Patient', 'Reception', 'Wait → Opto', 'Optometry', 'Wait → Doctor', 'Doctor', 'Wait → OPD', 'OPD', 'Total Wait', 'Total Time'],
    fetch: (p) => visitAPI.getVisits({ dateFrom: p.from, dateTo: p.to, limit: 5000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((v) => [
        `#${v.tokenNumber ?? '-'}`, v.visitId, fmtDate(v.visitDate), v.patient?.name || '',
        fmtDur(stageSeconds(v, 'reception')), fmtDur(waitAfterStage(v, 'reception')),
        fmtDur(stageSeconds(v, 'optometrist')), fmtDur(waitAfterStage(v, 'optometrist')),
        fmtDur(stageSeconds(v, 'doctor')), fmtDur(waitAfterStage(v, 'doctor')),
        fmtDur(stageSeconds(v, 'opd') ?? stageSeconds(v, 'dispensing')),
        fmtDur(waitSeconds(v)), fmtDur(totalSeconds(v)),
      ]),
    summary: (d) => {
      const avg = (stage) => {
        const vals = d.map((v) => stageSeconds(v, stage)).filter((x) => x != null);
        return vals.length ? fmtDur(vals.reduce((a, b) => a + b, 0) / vals.length) : '-';
      };
      return [
        { label: 'Visits', value: d.length },
        { label: 'Avg Reception', value: avg('reception') },
        { label: 'Avg Optometry', value: avg('optometrist') },
        { label: 'Avg Doctor', value: avg('doctor') },
        { label: 'Avg OPD', value: avg('opd') },
        { label: 'Avg Waiting', value: d.length ? fmtDur(d.reduce((s, v) => s + waitSeconds(v), 0) / d.length) : '-' },
        { label: 'Avg Total Visit', value: d.length ? fmtDur(d.reduce((s, v) => s + totalSeconds(v), 0) / d.length) : '-' },
      ];
    },
  },
  {
    key: 'staffTime',
    label: 'Staff Time Summary',
    icon: <PaymentsIcon />,
    color: 'error',
    columns: ['Staff', 'Role', 'Stage', 'Avg Time per Patient', 'Patients Handled'],
    fetch: (p) => analyticsAPI.getStaffPerformance({ startDate: p.from, endDate: p.to }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((s) => [
        s.staffName, s.role, s.stage, fmtDur(s.avgDuration), s.patientsHandled,
      ]),
    summary: (d) => [
      { label: 'Staff Active', value: new Set(d.map((s) => s.staffName)).size },
      { label: 'Stage Sessions', value: d.reduce((s, x) => s + (x.patientsHandled || 0), 0) },
      { label: 'Avg Stage Time', value: d.length ? fmtDur(d.reduce((s, x) => s + x.avgDuration, 0) / d.length) : '-' },
    ],
  },
  {
    key: 'medicines',
    label: 'Medicine Master',
    icon: <MedicationIcon />,
    color: 'success',
    noDateFilter: true,
    columns: ['Name', 'Generic Name', 'Type', 'Strength', 'Manufacturer', 'Status'],
    fetch: () => medicineAPI.getMedicines({ limit: 2000 }),
    getData: (res) => res.data.data,
    rows: (d) =>
      d.map((m) => [m.name, m.genericName || '', m.type, m.strength || '', m.manufacturer || '', m.isActive ? 'Active' : 'Inactive']),
    summary: (d) => [
      { label: 'Total Medicines', value: d.length },
      { label: 'Active', value: d.filter((m) => m.isActive).length },
      { label: 'Types', value: new Set(d.map((m) => m.type)).size },
    ],
  },
  {
    key: 'dispensing',
    label: 'Dispensing / Sales',
    icon: <PointOfSaleIcon />,
    color: 'primary',
    columns: ['Date', 'Visit', 'Patient', 'Items', 'Total', 'Paid', 'Payment Status', 'Dispensed By'],
    fetch: (p) => dispensingAPI.getRecords({ limit: 5000 }),
    getData: (res) => res.data.data,
    filterByDate: true,
    rows: (d) =>
      d.map((r) => [
        fmtDate(r.createdAt), r.visit?.visitId || '', r.patient?.name || '', (r.items || []).length,
        inr(r.totalAmount), inr(r.paidAmount), r.paymentStatus || '', r.dispensedBy?.name || '',
      ]),
    summary: (d) => [
      { label: 'Records', value: d.length },
      { label: 'Items Dispensed', value: d.reduce((s, r) => s + (r.items || []).length, 0) },
      { label: 'Sales Value', value: inr(d.reduce((s, r) => s + (r.totalAmount || 0), 0)) },
      { label: 'Collected', value: inr(d.reduce((s, r) => s + (r.paidAmount || 0), 0)) },
    ],
  },
];

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const Reports = () => {
  const [reportKey, setReportKey] = useState('visits');
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null); // { def, data, rows, summary, clinic }
  const [printData, setPrintData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const def = REPORTS.find((r) => r.key === reportKey);

  const generate = async () => {
    try {
      setLoading(true);
      const res = await def.fetch({ from: dateFrom, to: dateTo });
      let data = def.getData(res);
      if (def.filterByDate) {
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        data = data.filter((r) => {
          const d = new Date(r.createdAt);
          return (!from || d >= from) && (!to || d <= to);
        });
      }
      const settingsRes = await settingsAPI.getSettings().catch(() => null);
      setReport({
        def,
        data,
        rows: def.rows(data),
        summary: def.summary(data),
        clinic: settingsRes?.data?.data,
      });
    } catch (error) {
      showSnackbar('Failed to generate report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const csv = [report.def.columns, ...report.rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${report.def.key}_report_${dateFrom}_to_${dateTo}.csv`;
    link.click();
  };

  const handlePrint = () => {
    setPrintData(report);
    setTimeout(() => window.print(), 100);
  };

  return (
    <Box>
      {/* Header */}
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
          }}>
            <AssessmentIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700">Reports</Typography>
            <Typography variant="body2" color="text.secondary">Generate module-wise reports for any date range</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Report Type"
            value={reportKey}
            onChange={(e) => { setReportKey(e.target.value); setReport(null); }}
            sx={{ minWidth: 230 }}
          >
            {REPORTS.map((r) => (
              <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
            ))}
          </TextField>
          {!def.noDateFilter && (
            <>
              <TextField type="date" size="small" label="From" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" label="To" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </>
          )}
          <Button
            variant="contained"
            onClick={generate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AssessmentIcon />}
          >
            {loading ? 'Generating…' : 'Generate Report'}
          </Button>
          {report && (
            <>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}>
                Export CSV
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
                Print
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Report output */}
      {report && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            {report.summary.map((s, i) => (
              <StatCard
                key={s.label}
                label={s.label}
                value={s.value}
                icon={i === 0 ? report.def.icon : undefined}
                color={report.def.color}
              />
            ))}
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #E9E9F2', display: 'flex', justifyContent: 'space-between' }}>
              <Typography fontWeight={700}>{report.def.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {report.def.noDateFilter ? 'All records' : `${fmtDate(dateFrom)} — ${fmtDate(dateTo)}`} • {report.rows.length} rows
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {report.def.columns.map((c) => (
                      <TableCell key={c} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={report.def.columns.length} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">No records found for this range</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.rows.map((row, i) => (
                      <TableRow key={i} hover>
                        {row.map((cell, j) => (
                          <TableCell key={j} sx={{ whiteSpace: 'nowrap' }}>{cell ?? ''}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Printable report */}
      {printData && (
        <PrintPortal>
          <Box sx={{ p: 3, fontFamily: 'Arial, sans-serif' }}>
            <Typography variant="h5" fontWeight="bold" align="center">
              {printData.clinic?.clinicName || 'EyeDealist Clinic'}
            </Typography>
            {printData.clinic?.address && (
              <Typography variant="body2" align="center" color="text.secondary">
                {printData.clinic.address}
                {printData.clinic.phone ? ` • ${printData.clinic.phone}` : ''}
                {printData.clinic.gstin ? ` • GSTIN: ${printData.clinic.gstin}` : ''}
              </Typography>
            )}
            <Typography variant="h6" align="center" sx={{ mt: 2, mb: 1 }}>
              {printData.def.label}
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
              {printData.def.noDateFilter ? 'All records' : `Period: ${fmtDate(dateFrom)} — ${fmtDate(dateTo)}`}
              {' • '}Generated: {fmtDate(new Date())}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {printData.def.columns.map((c) => (
                    <TableCell key={c} sx={{ fontWeight: 'bold', borderBottom: '2px solid #000' }}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {printData.rows.map((row, i) => (
                  <TableRow key={i}>
                    {row.map((cell, j) => <TableCell key={j}>{cell}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {printData.summary.map((s) => `${s.label}: ${s.value}`).join('  •  ')}
            </Typography>
          </Box>
        </PrintPortal>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports;
